import { useEffect, useState } from 'react';
import axiosInstance, { endpoints } from 'src/utils/axios';
import { useAuth } from 'src/hooks/useAuth';

/**
 * Hook to fetch and manage site settings
 * Returns site settings data including logo, icon, name, etc.
 */
// Module-level cache
let globalSettings = null;
let globalPromise = null;
let globalError = null;

export function clearSiteSettingsCache() {
  globalSettings = null;
  globalPromise = null;
  globalError = null;
}

export function setGlobalSiteSettings(newSettings) {
  globalSettings = newSettings;
  globalPromise = null;
  globalError = null;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('site-settings-updated', { detail: newSettings }));
  }
}

/**
 * Hook to fetch and manage site settings
 * Returns site settings data including logo, icon, name, etc.
 * Uses module-level caching to prevent redundant fetches.
 */
export function useSiteSettings() {
  const { isAuthenticated } = useAuth();

  const [state, setState] = useState({
    settings: globalSettings,
    loading: !globalSettings && !globalError && isAuthenticated,
    error: globalError
  });

  useEffect(() => {
    const handleSettingsUpdated = (e) => {
      const updated = e?.detail || globalSettings;
      if (updated) {
        setState({ settings: updated, loading: false, error: null });
      } else {
        clearSiteSettingsCache();
        setState({ settings: null, loading: true, error: null });
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('site-settings-updated', handleSettingsUpdated);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('site-settings-updated', handleSettingsUpdated);
      }
    };
  }, []);

  useEffect(() => {
    // Skip fetching if user is not authenticated (e.g., on login page)
    if (!isAuthenticated) {
      setState(prev => (prev.loading ? { ...prev, loading: false } : prev));
      return;
    }

    // If we already have settings in cache, use them immediately
    if (globalSettings) {
      setState(prev =>
        (prev.loading || !prev.settings || prev.settings !== globalSettings)
          ? { settings: globalSettings, loading: false, error: null }
          : prev
      );
      return;
    }

    // If we have a cached error, return it
    if (globalError) {
      setState(prev =>
        (prev.loading || !prev.error)
          ? { settings: null, loading: false, error: globalError }
          : prev
      );
      return;
    }

    // Initialize fetch if not already in progress
    if (!globalPromise) {
      globalPromise = axiosInstance.post(endpoints.siteSettings.get)
        .then((response) => {
          if (response.data.success && response.data.data) {
            globalSettings = response.data.data;
          }
          return globalSettings;
        })
        .catch((err) => {
          console.error('Error fetching site settings:', err);
          globalError = err;
          throw err;
        });
    }

    // Subscribe to the promise (whether new or existing)
    setState(prev => ({ ...prev, loading: true }));

    globalPromise
      .then((data) => {
        if (data) {
          setState({ settings: data, loading: false, error: null });
        }
      })
      .catch((err) => {
        setState({ settings: null, loading: false, error: err });
      });

  }, [isAuthenticated]);

  return state;
}

/**
 * Hook to update document title and favicon dynamically
 * @param {string} title - Page title
 * @param {string} favicon - Favicon URL
 */
export function useDocumentMeta(title, favicon) {
  useEffect(() => {
    if (title) {
      document.title = title;
    }
  }, [title]);

  useEffect(() => {
    if (favicon) {
      // Update all favicon links
      const links = document.querySelectorAll("link[rel*='icon']");
      links.forEach((link) => {
        link.href = favicon;
      });

      // If no favicon links exist, create one
      if (links.length === 0) {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = favicon;
        document.head.appendChild(link);
      }
    }
  }, [favicon]);
}
